import { Injectable } from '@angular/core';
import { Headers, Http, RequestOptionsArgs, Response } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { PagamentoParcial } from '../bf-extrato-cli/components/detalhe-parcela/detalhe-parcela.model';
import { Config } from './config';
import { SessaoService } from './sessao.service';

export interface PagamentoParcelaApiPagamento {
  tipoSituacaoPagamento?: string;
  codigoSituacaoPagamento?: string;
  codigoPagamento?: string;
  codigoContrato?: string;
  parcelaContrato?: string | number;
  valorIofEfetivoPago?: string | number;
  valorIofEfetivoParcela?: string | number;
  valorJurosPagoCobranca?: string | number;
  valorJurosRemuneratoriosCobrados?: string | number;
  valorMoraProvisoriaPago?: string | number;
  valorJurosMoraProvisionados?: string | number;
  valorMultaPagoCobranca?: string | number;
  valorMultaCobrada?: string | number;
  dataPagamento?: string;
  valorEfetivoPago?: string | number;
  valorPrincipalEfetivoPago?: string | number;
  formaPagamento?: string;
  naturezaPagamentoEmprestimo?: string;
  numeroParcelaPagamento?: string;
  valorPrincipalParcelaVencida?: string | number;
  valorAcumuladoPermanenciaCobranca?: string | number;
  valorAcumuladoMultaCobranca?: string | number;
  valorAcumuladoMoraCobranca?: string | number;
  valorIofParcelaVencida?: string | number;
}

export interface PagamentosParcelaResultado {
  totalPagamentos: number;
  quantoFaltaPagar?: number;
  pagamentos: PagamentoParcial[];
}

export interface PagamentoParcelaApiResponse {
  retornoProcesso?: {
    codigoRetorno?: string;
    mensagemErroCodigo?: string;
    mensagemErroTexto?: string;
  };
  totalPagamentos?: string | number;
  quantoFaltaPagar?: string | number;
  pagamentos?: PagamentoParcelaApiPagamento[];
  indicadorFimLista?: string;
}

@Injectable()
export class PagamentoParcelaService {

  private static readonly FORMAS_PAGAMENTO: { [codigo: number]: string } = {
    9: 'Boleto',
    10: 'Carnê',
    22: 'Portabilidade',
    25: 'Boleto',
    40: 'Débito em Conta',
    44: 'Assessoria',
    46: 'Contrato Renegociado',
    47: 'Contrato Cedido',
    55: 'Contrato Cancelado',
    56: 'Contrato Renegociado',
    60: 'Contrato Cancelado',
    69: 'Contrato Encerrado',
    70: 'Contrato Encerrado',
    77: 'Seguradora',
    78: 'Outros, Reembolso Realizado',
    82: 'Contrato encerrado'
  };

  // Canal exigido pelo filtro de canal do Conecta na API de pagamentos
  private static readonly LOGIN_CANAL: string = '536';

  constructor(
      private http: Http,
      private sessaoService: SessaoService) {
  }

  listarPagamentosParcela(nuContrato: number | string, numeroParcela: number | string): Observable<PagamentosParcelaResultado> {
    const numeroParcelaFormatado = this.formatarNumeroParcela(numeroParcela);
    const nuContratoFormatado = this.formatarNumeroContrato(nuContrato);
    const url = `${Config.EXTRATOCONTRATO_PATH}/nuCTRL/${this.sessaoService.sessao.autenticado.nuCTRL}/contratos/${nuContratoFormatado}/parcelas/${numeroParcelaFormatado}/pagamentos`;
 
    const headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    headers.append('x-login-canal', PagamentoParcelaService.LOGIN_CANAL);

    const requestArgs: RequestOptionsArgs = { headers: headers };

    return this.http.get(url, requestArgs)
      .map((response: Response) => response.json())
      .map((body: any) => {
        // A API pode retornar o JSON serializado como string (double-encoded)
        if (typeof body === 'string') {
          try {
            body = JSON.parse(body);
          } catch (e) {
            body = null;
          }
        }

        // A API pode retornar o array de pagamentos direto ou encapsulado no objeto "pagamentos"
        const pagamentos: PagamentoParcelaApiPagamento[] = Array.isArray(body)
          ? body
          : (body && Array.isArray(body.pagamentos) ? body.pagamentos : []);

        const totalPagamentos = !Array.isArray(body) && body && body.totalPagamentos !== undefined
          ? this.converterValor(body.totalPagamentos)
          : pagamentos.reduce((total, p) => total + this.converterValor(p.valorEfetivoPago), 0);

        const pagamentosMapeados = pagamentos.map((pagamento, index) => ({
          numero: index + 1,
          dataPagamento: this.formatarData(pagamento.dataPagamento),
          valorPrincipal: this.converterValor(pagamento.valorPrincipalEfetivoPago),
          jurosRemuneratorios: this.converterValor(pagamento.valorJurosRemuneratoriosCobrados),
          jurosMoratorios: this.converterValor(pagamento.valorJurosMoraProvisionados),
          multa: this.converterValor(pagamento.valorMultaCobrada),
          iof: this.converterValor(pagamento.valorIofEfetivoParcela),
          valorPagoParcial: this.converterValor(pagamento.valorEfetivoPago),
          formaPagamento: this.descreverFormaPagamento(pagamento.formaPagamento),
          tipoSituacaoPagamento: pagamento.tipoSituacaoPagamento || pagamento.codigoSituacaoPagamento,
          codigoPagamento: pagamento.codigoPagamento,
          numeroParcelaPagamento: pagamento.numeroParcelaPagamento || pagamento.parcelaContrato
        } as any));

        return {
          totalPagamentos: Number(totalPagamentos.toFixed(2)),
          quantoFaltaPagar: !Array.isArray(body) && body && body.quantoFaltaPagar !== undefined
            ? this.converterValor(body.quantoFaltaPagar)
            : undefined,
          pagamentos: pagamentosMapeados
        };
      });
  }

  private descreverFormaPagamento(codigo: string): string {
    const codigoTexto = (codigo || '').trim();
    const codigoNumerico = /^\d+$/.test(codigoTexto) ? Number(codigoTexto) : NaN;

    if (!isNaN(codigoNumerico)) {
      return PagamentoParcelaService.FORMAS_PAGAMENTO[codigoNumerico] || 'Outros';
    }

    return codigoTexto;
  }

  private formatarNumeroContrato(nuContrato: number | string): string {
    const somenteDigitos = String(nuContrato === null || nuContrato === undefined ? '' : nuContrato).replace(/\D/g, '');

    // O contrato da sessao possui o digito verificador no final, porem a API de pagamentos espera o numero sem o DV
    if (somenteDigitos.length > 9) {
      return somenteDigitos.substring(0, somenteDigitos.length - 1);
    }

    return somenteDigitos;
  }

  private formatarNumeroParcela(numeroParcela: number | string): string {
    const valorTexto = String(numeroParcela === null || numeroParcela === undefined ? '' : numeroParcela).trim();
    let valorFormatado = valorTexto;
    while (valorFormatado.length < 3) {
      valorFormatado = '0' + valorFormatado;
    }
    return valorFormatado;
  }

  private formatarData(valor: string): string {
    if (!valor) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
      const [ano, mes, dia] = valor.split('-');
      return `${dia}/${mes}/${ano}`;
    }

    if (valor.length !== 8) {
      return '';
    }

    const ano = valor.substring(0, 4);
    const mes = valor.substring(4, 6);
    const dia = valor.substring(6, 8);
    return `${dia}/${mes}/${ano}`;
  }

  private converterValor(valor: string | number): number {
    if (valor === null || valor === undefined || valor === '') {
      return 0;
    }

    if (typeof valor === 'number') {
      return Number(valor.toFixed(2));
    }

    const valorTexto = String(valor).trim();
    const valorSemFormatacao = valorTexto.replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, '');

    if (!valorSemFormatacao) {
      return 0;
    }

    const valorNumerico = Number(valorSemFormatacao);
    if (isNaN(valorNumerico)) {
      return 0;
    }
    if (/^\d+$/.test(valorTexto) && valorTexto.length >= 10) {
      return Number((valorNumerico / 100).toFixed(2));
    }

    return Number(valorNumerico.toFixed(2));
  }
}



// WEBPACK FOOTER //
// /home/runner/_work/fini-sta-01-estatico-sitecliente/fini-sta-01-estatico-sitecliente/src/app/services/pagamento-parcela.service.ts